import type React from 'react';
import { useCallback, useEffect, useRef, type FocusEvent, type KeyboardEvent, type MouseEvent } from 'react';
import styled, { useTheme } from 'styled-components';
import { useDynamicCallback } from '../../../hooks';
import { Z_INDEX } from '../../../styles';
import { Button, InternalButtonSpan } from '../../Button';
import type { FilterableSelectProperty } from '../../Filters';
import { FilterCheckboxDropdown } from '../../Filters/FilterBuilder/RHS/FilterCheckboxDropdown';
import { OverflowItemsBox } from '../../OverflowItemsBox';
import { useDropdownPopper } from '../Dropdown';
import { useMultiSelectAutocomplete } from '../MultiSelect/useMultiselectAutocomplete';
import { useMultiSelectSelectionManager } from '../MultiSelect/useMultiSelectSelectionManager';
import { FormControlSizes } from '../types';

type MultiSelectCheckboxDropdownProps<T> = {
  prefix: React.ReactNode;
  defaultLabel?: React.ReactNode;
  property: FilterableSelectProperty<string, T>;
  selections: T[];
  onSelectionsChange: (newSelections: T[]) => void;
  onDropdownTabOut?: (event: KeyboardEvent<HTMLElement>) => void;

  labelFontSize?: number;
  chipFontSize?: number;

  /* The data-testid to place on the button (optional) */
  buttonTestID?: string;
  /* The data-testid to place on the dropdown (optional) */
  dropdownTestID?: string;
  size?: FormControlSizes;
  /** When provided, items for which this returns true will have their checkbox disabled. */
  isItemDisabled?: (item: T) => boolean;
  /** Render the dropdown menu in a top-level portal so it escapes ancestor `overflow` clipping. */
  portalize?: boolean;
};

/** Dropdown component to support MultiSelect scenarios.
 * - This is distinct from the MultiSelect component as it doesn't involve chip selection (the box is a simple managed control button),
 * and is based on the PropertyMultiSelectList implementation used in filters  */
export function MultiSelectCheckboxDropdown<T = string>({
  prefix,
  defaultLabel = 'Select...',
  property,
  selections,
  onSelectionsChange,
  labelFontSize,
  chipFontSize,
  buttonTestID,
  dropdownTestID,
  size = FormControlSizes.Default,
  isItemDisabled,
  portalize,
}: MultiSelectCheckboxDropdownProps<T>) {
  const theme = useTheme();
  const { getOptionLabel } = property;
  const inputRef = useRef<HTMLInputElement>(null);
  const refElement = useRef<HTMLButtonElement>(null);

  const { dropdownItems, handleSelectionsChange, handleOpenChange } = useMultiSelectSelectionManager<T>({
    property,
    selections,
    onSelectionsChange,
  });

  const { autocompleteOutput, multipleSelectionOutput } = useMultiSelectAutocomplete<T>({
    selections: selections,
    items: dropdownItems,
    initialSortByLabel: false,
    getLabel: getOptionLabel,
    getDescription: property.getOptionDescription,
    getGroup: property.getOptionGroup,
    groupSorter: property.groupSorter,
    onChange: handleSelectionsChange,
    onIsOpenChange: handleOpenChange,
    clearInputAfterSelection: false,
    removeItemOnInputBackspace: false,
    highlightInputTextAfterSelection: true,
    inputRef,
    isItemDisabled,
  });

  const { isOpen, openMenu, closeMenu } = autocompleteOutput;

  const dropdownPopper = useDropdownPopper({
    isOpen,
    referenceElement: refElement.current,
    dropdownWidth: property.dropdownOptions?.dropdownWidth,
    dropdownPlacement: 'bottom-start',
    onClickOutside: closeMenu,
  });

  const updatePopperPosition = dropdownPopper.popper.update;

  useEffect(() => {
    if (selections) {
      // every time `the clauses change, we update the popper position
      updatePopperPosition && updatePopperPosition();
    }
  }, [selections, updatePopperPosition]);

  const handleOpenClick = useCallback(
    (e: MouseEvent<HTMLButtonElement> | FocusEvent<HTMLButtonElement>) => {
      e.preventDefault();
      updatePopperPosition && updatePopperPosition();
      openMenu();
      setTimeout(() => inputRef.current?.focus(), 0);
    },
    [openMenu, updatePopperPosition]
  );

  const handleClearAll = useCallback(() => {
    onSelectionsChange([]);
  }, [onSelectionsChange]);

  const handleSelectAll = useCallback(() => {
    onSelectionsChange(property.options);
  }, [onSelectionsChange, property.options]);

  const getOptionsLabel = useDynamicCallback(getOptionLabel);

  return (
    <>
      <MultiSelectCheckboxDropdownButton
        data-testid={buttonTestID ?? 'base-select-button'}
        ref={refElement}
        onClick={e => handleOpenClick(e)}
        flex="auto"
        width="100%"
        isOpen={isOpen}
        size={size}
      >
        <OverflowItemsBox
          prefix={prefix}
          defaultLabel={defaultLabel}
          items={multipleSelectionOutput.selectedItems}
          getItemLabel={getOptionsLabel}
          tooltipZIndex={Z_INDEX.dropdown + 1}
          labelFontSize={labelFontSize ?? theme.fontSizeSmall}
          chipFontSize={chipFontSize ?? theme.fontSizeTiny}
        />
      </MultiSelectCheckboxDropdownButton>
      <FilterCheckboxDropdown
        {...autocompleteOutput}
        {...dropdownPopper}
        portalize={portalize}
        testID={dropdownTestID}
        property={property}
        selectedItems={multipleSelectionOutput.selectedItems}
        inputRef={inputRef}
        onSelectAll={handleSelectAll}
        onClearAll={handleClearAll}
        onTabOut={closeMenu}
        getDropdownProps={multipleSelectionOutput.getDropdownProps}
        addSelectedItem={multipleSelectionOutput.addSelectedItem}
        removeSelectedItem={multipleSelectionOutput.removeSelectedItem}
        maxHeight={property.dropdownOptions?.maxHeight}
        groupMaxHeight={property.dropdownOptions?.groupMaxHeight}
        isItemDisabled={isItemDisabled}
      />
    </>
  );
}

const MultiSelectCheckboxDropdownButton = styled(Button)<{
  isOpen: boolean;
}>`
  padding: ${({ theme }) => `${theme.spacing4}px ${theme.spacing8}px`};
  justify-content: flex-start;
  text-align: left;
  ${({ theme, isOpen }) => {
    return isOpen
      ? `
      border-color: ${theme.borderColorSelectHover};
      background: ${theme.backgroundSelectHover};
      color: ${theme.colorTextImportant};
    `
      : ``;
  }};

  > ${InternalButtonSpan} {
    display: unset;
    flex: auto;
  }
`;
